/* Novium — log of engraving characters the site rejects (v2.2, 19 Oct 2026: font name the same in every language).
   Standalone: needs nothing from engrave-mobile.js and changes nothing in cw-script.js.

   How it knows what was rejected: the customer's field (.input_text) keeps exactly what was
   typed or pasted; the theme's own rule (cw-script.js) writes the cleaned text into the grey
   box (.viewinput_text). Whatever is in the field but not in the grey box was rejected, so this
   follows the theme's rule automatically when new characters are allowed.

   What is sent (nothing read from the customer's device, nothing that identifies anyone):
   the refused character, typed or pasted, the product page, our store and its Shopify country
   setting, the font chosen in our form, and how the visit ended:
     - "Fixed and confirmed"
     - "Closed without engraving" (the popup was closed)
     - "Left or switched away" (back, tab closed, page left, or another tab/app opened; the
       browser cannot say whether they come back, so a later Confirm is not recorded)
   Rows are sent once, when the outcome is known, so every row carries its outcome.
   Each character is logged once per page view. If Google does not answer, nothing changes. */
(function () {
  var ENDPOINT = 'https://script.google.com/macros/s/AKfycbxz_eApgs8g2NFYllPMDf74RCMJwic5IWlxYnbWzyWfRdp_PgfNLZHBFdymkqWnSiz9/exec';
  if (ENDPOINT.indexOf('https://') !== 0) return;

  var seen = {};          // characters already logged on this page view
  var pending = [];       // refused characters waiting for the outcome
  var how = 'typed';      // typed / pasted / other, from the last change to the field

  /* one item per visible character: 👍🏽, 👨‍👩‍👧 and ❤️ stay whole */
  function graphemes(s) {
    if (window.Intl && Intl.Segmenter) {
      return Array.from(new Intl.Segmenter(undefined, { granularity: 'grapheme' }).segment(s), function (x) { return x.segment; });
    }
    return Array.from(s);
  }

  function kind(g) {
    try {
      if (/\p{Extended_Pictographic}|\p{Regional_Indicator}/u.test(g)) return 'Emoji';
      if (/^[\p{Cf}\p{Zs}\p{Zl}\p{Zp}\p{Cc}\p{M}]+$/u.test(g)) return 'Invisible / space';
      if (/\p{Script=Hiragana}|\p{Script=Katakana}/u.test(g)) return 'Japanese';
      if (/\p{Script=Han}/u.test(g)) return 'Chinese / Japanese (kanji)';
      if (/\p{Script=Hangul}/u.test(g)) return 'Korean';
      if (/\p{Script=Cyrillic}/u.test(g)) return 'Cyrillic';
      if (/\p{Script=Greek}/u.test(g)) return 'Greek';
      if (/\p{Script=Arabic}/u.test(g)) return 'Arabic';
      if (/\p{Script=Hebrew}/u.test(g)) return 'Hebrew';
      if (/\p{Script=Thai}/u.test(g)) return 'Thai';
      if (/\p{Script=Devanagari}|\p{Script=Bengali}|\p{Script=Tamil}/u.test(g)) return 'Indian scripts';
      if (/\p{Script=Latin}/u.test(g)) return 'Latin letter (accent)';
      if (/\p{N}/u.test(g)) return 'Number';
      if (/\p{S}|\p{P}/u.test(g)) return 'Symbol';
    } catch (e) {}
    return 'Other';
  }

  /* invisible characters get a short readable name in the Sheet instead of an empty-looking cell
     (the Sheet keeps 16 characters of this cell, so every name fits in [ ]) */
  var NAMES = {
    ' ': 'no-break space', '　': 'wide space', '​': 'zero-width', '‌': 'non-joiner',
    '‍': 'joiner', '‎': 'LTR mark', '‏': 'RTL mark', '️': 'emoji marker',
    '︎': 'text marker', ' ': 'thin space', ' ': 'narrow space', ' ': 'en space',
    ' ': 'em space', '⁠': 'word joiner', '﻿': 'zero-width', '­': 'soft hyphen',
    '\t': 'tab', '\n': 'line break', '\r': 'line break'
  };
  function shown(g, type) {
    if (type !== 'Invisible / space') return g;
    var c = Array.from(g);
    if (c.length > 1) return '[invisible x' + c.length + ']';
    return '[' + (NAMES[c[0]] || 'invisible') + ']';
  }

  function codes(g) {
    return Array.from(g).map(function (c) { return 'U+' + c.codePointAt(0).toString(16).toUpperCase().padStart(4, '0'); }).join(' ');
  }

  /* walk the typed text and the grey box together: a character the grey box skipped was
     rejected. Stops once the grey box is full (18), since the rest was cut for length, not refused. */
  function rejected(raw, kept) {
    var out = [], k = 0, a = graphemes(raw), b = graphemes(kept);
    for (var i = 0; i < a.length; i++) {
      if (k < b.length && a[i] === b[k]) { k++; continue; }
      if (k >= b.length && b.length >= 18) break;
      out.push(a[i]);
    }
    return out;
  }

  function greyBox(field) {
    var modal = field.closest('.modal') || document;
    return modal.querySelector('.viewinput_text') || document.querySelector('.viewinput_text');
  }

  /* the font selected in our form, with the same name in every store language (the buttons'
     labels are translated, their classes are not): Manuscrit = Rage, Moderne = Avenir */
  function font() {
    var li = document.querySelector('.modal.is-visible .engravfontvariant li.active') ||
             document.querySelector('.engravfontvariant li.active');
    if (!li) return '';
    if (li.classList.contains('cursivefont')) return 'Manuscrit';
    if (li.classList.contains('regularfont')) return 'Moderne';
    var input = li.querySelector('input');
    return ((input && input.value) || li.textContent || '').trim().slice(0, 30);   // unknown button: its label
  }

  function send(outcome, useBeacon) {
    if (!pending.length) return;
    var body = JSON.stringify({
      items: pending.splice(0, 30),
      product: (location.pathname || '').slice(0, 120),
      store: (location.hostname || '').slice(0, 60),
      country: ((window.Shopify && window.Shopify.country) || '').slice(0, 5),   // the store's market setting
      font: font(),
      outcome: outcome
    });
    pending = [];
    try {
      if (useBeacon && navigator.sendBeacon) { navigator.sendBeacon(ENDPOINT, body); return; }
      fetch(ENDPOINT, { method: 'POST', mode: 'no-cors', keepalive: true, body: body,
                        headers: { 'Content-Type': 'text/plain;charset=utf-8' } }).catch(function () {});
    } catch (e) {}
  }

  function check(field, via) {
    var box = greyBox(field);
    if (!box) return;
    rejected(field.value || '', box.value || '').forEach(function (g) {
      if (seen[g]) return;
      seen[g] = true;
      var t = kind(g);
      pending.push({ c: shown(g, t), code: codes(g), type: t, how: via });
    });
  }

  document.addEventListener('beforeinput', function (e) {
    if (!e.target.matches || !e.target.matches('.input_text')) return;
    how = /Paste|Drop/.test(e.inputType || '') ? 'pasted'
        : /insertText|insertCompositionText|insertReplacementText/.test(e.inputType || '') ? 'typed' : 'other';
  }, true);

  /* runs after the theme's own handler has filled the grey box */
  document.addEventListener('input', function (e) {
    if (!e.target.matches || !e.target.matches('.input_text')) return;
    /* a letter still being composed is not what the customer typed yet: the ` of è on a French
       Mac keyboard, the ´ of é, a Japanese word before it is chosen. Checked when it is finished. */
    if (e.isComposing || (e.inputType || '') === 'insertCompositionText') return;
    var field = e.target, it = e.inputType || '';     // typed or pasted, read now, not later
    var via = /Paste|Drop/.test(it) ? 'pasted' : /insertText|Replacement/.test(it) ? 'typed' : how;
    setTimeout(function () { check(field, via); }, 0);
  }, true);
  document.addEventListener('compositionend', function (e) {
    if (!e.target.matches || !e.target.matches('.input_text')) return;
    var field = e.target;
    setTimeout(function () { check(field, 'typed'); }, 0);
  }, true);

  /* outcome 1: Confirm with no refused character left (the theme only accepts it then) */
  document.addEventListener('click', function (e) {
    var btn = e.target.closest && e.target.closest('.confirmbtn');
    if (!btn || !pending.length || btn.disabled) return;
    var field = document.querySelector('.modal.is-visible .input_text') || document.querySelector('.input_text');
    var box = field && greyBox(field);
    if (!field || !box) return;
    var raw = field.value || '';
    if (raw.trim() && raw.length <= 18 && !rejected(raw, box.value || '').length) send('Fixed and confirmed', false);   // same checks as the theme's Confirm
  }, true);

  /* outcome 2: the engraving popup closes without that */
  function watchModals() {
    document.querySelectorAll('.modal').forEach(function (m) {
      if (m.__nvmLog) return;
      m.__nvmLog = true;
      var open = m.classList.contains('is-visible');
      new MutationObserver(function () {
        var now = m.classList.contains('is-visible');
        if (open && !now) setTimeout(function () { send('Closed without engraving', false); }, 0);   // after a Confirm click, nothing is left to send
        open = now;
      }).observe(m, { attributes: true, attributeFilter: ['class'] });
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', watchModals); else watchModals();
  var later = false;                                   // popups added later: look again, at most twice a second
  new MutationObserver(function () {
    if (later) return; later = true;
    setTimeout(function () { later = false; watchModals(); }, 500);
  }).observe(document.documentElement, { childList: true, subtree: true });

  /* outcome 3: the customer leaves the page or switches to another tab/app */
  window.addEventListener('pagehide', function () { send('Left or switched away', true); });
  document.addEventListener('visibilitychange', function () { if (document.visibilityState === 'hidden') send('Left or switched away', true); });
})();
